import * as React from 'react';
import { Prompt, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Card } from 'material-ui/Card';
import { get, isArray, sortBy, each, omit } from 'lodash';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { compose, withHandlers } from 'recompose';
import { reduxForm, getFormValues, FieldArray } from 'redux-form';
import { RemoteAutoCompleteField, RemoteAutoComplete } from '@components/form';

import * as T from './_dayplansTypes';
import { required } from '@utils/validators';
import { saveDayPlans } from '@modules/hr-dayplans/_dayplansActions';
import * as moment from 'moment';
import { dayplansPath } from './DayplansGridSingle';
import { withNotifications } from '@components/hoc/withNotifications';

const linkBack = dayplansPath;

const enhance = compose<T.DayplansFormProps, T.DayplansFormProps>(
  withRouter,
  withNotifications({ saveDayPlans }),
  withHandlers({
    onSubmit: ({ history, saveDayPlansNotify }) => values => {
      let allDayPlans: any[] = [];
      each(get(values, 'employees'), employee => {
        allDayPlans = [...allDayPlans, ...employee.dayPlans];
      });
      saveDayPlansNotify(allDayPlans).then(() => history.push(linkBack), error => console.debug(error));
    }
  }),
  reduxForm({
    form: 'dayplansGrid'
  }),
  connect(
    state => ({
      employees: get(getFormValues('dayplansGrid')(state), 'employees')
    }),
    {}
  )
);

const renderEmployees = ({ fields, meta: { error }, employees, change }) => (
  <>
    {fields.map((dayPlan, index) => {
      const dayPlanObj = fields.get(index);
      const {
        shiftCycle: { from: shiftCycleBeginning, to: shiftCycleEnd },
        employee
      } = dayPlanObj;
      let iterator = 0,
        dayIterator = 0;
      return (
        <Row key={`dayPlan${index}`} style={{ marginBottom: 20 }}>
          <Col md={4} xl={2}>
            <div style={{ display: 'flex' }} key={index}>
              <div key={index} style={{ width: '100%', marginRight: 5 }}>
                <RemoteAutoCompleteField
                  id="type"
                  caching={true}
                  name={`${dayPlan}.employee.id`}
                  label={`Zaměstnanec ${index + 1} *`}
                  disabled={true}
                  fullWidth={true}
                  component={RemoteAutoComplete}
                  url="/metropolis/api/hr/employees"
                  labelFunction={o => (o ? `${get(o, 'firstName', '')} ${get(o, 'lastName', '')}` : '')}
                  valueFunction={o => get(o, 'id')}
                  validate={[required]}
                  arrowstyle={{
                    color: '#e1e0e1',
                    position: 'absolute',
                    right: 0,
                    top: 32
                  }}
                />
              </div>
            </div>
          </Col>

          <Col md={4} xl={2}>
            <div style={{ display: 'flex' }} key={index}>
              <div key={index} style={{ width: '100%', marginRight: 5 }}>
                <RemoteAutoCompleteField
                  id="type"
                  caching={true}
                  name={`${dayPlan}.calendar`}
                  label="Kalendář *"
                  fullWidth={true}
                  component={RemoteAutoComplete}
                  url="/metropolis/api/hr/workplan/calendar"
                  labelFunction={o => (o ? get(o, 'label') : undefined)}
                  valueFunction={o => o}
                  validate={[required]}
                  arrowstyle={{
                    color: '#e1e0e1',
                    position: 'absolute',
                    right: 0,
                    top: 32
                  }}
                  onChange={(_, calendar) => {
                    const days: any[] = [];
                    each(sortBy(get(calendar, 'weekItems', []), ['order']), (weekObject: any) =>
                      each(sortBy(get(weekObject, 'week.weekDays', []), ['order']), ({ day }: any) => {
                        const currentDay = moment.utc(shiftCycleBeginning).add(dayIterator, 'd');
                        if (!currentDay.isAfter(moment.utc(shiftCycleEnd))) {
                          days.push({
                            ...omit(day, 'id'),
                            visualizedDate: currentDay.format('YYYY-MM-DD'),
                            periods: day.periods.map(period => {
                              return {
                                ...omit(period, 'id'),
                                from: currentDay
                                  .hours(get(period, 'from').substring(0, 2))
                                  .minutes(get(period, 'from').substring(3, 5))
                                  .toDate(),
                                to: currentDay
                                  // Need to add one day if from > to
                                  // for example period begins at 22:00 and ends after midnight
                                  .add(get(period, 'from') > get(period, 'to') ? 1 : 0, 'd')
                                  .hours(get(period, 'to').substring(0, 2))
                                  .minutes(get(period, 'to').substring(3, 5))
                                  .toDate()
                              };
                            }),
                            employee
                          });
                        }
                        dayIterator++;
                      })
                    );
                    change(`employees[${index}].dayPlans`, days);
                  }}
                />
              </div>
            </div>
          </Col>
          <Col md={4} xl={8}>
            <div style={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto' }}>
              {sortBy(get(employees[index], 'calendar.weekItems', []), ['order']).map((weekObject: any) => (
                <div key={weekObject.id} style={{ marginRight: 10, marginBottom: 16 }}>
                  <div style={{ borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                    {get(weekObject, 'week.label')}
                  </div>
                  <div style={{ display: 'flex' }}>
                    {sortBy(get(weekObject, 'week.weekDays', []), ['order']).map((dayObject: any) => (
                      <div
                        key={get(dayObject, 'id')}
                        style={{
                          borderBottom: `5px solid ${get(dayObject, 'day.color', 'transparent')}`,
                          padding: `0 5px`
                        }}
                      >
                        <p>
                          {moment(shiftCycleBeginning)
                            .add(iterator++, 'd')
                            .format('DD.MM.')}
                        </p>
                        {get(dayObject, 'day.label')}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      );
    })}
    {error && <div className="error">{error}</div>}
  </>
);

const View: React.SFC<T.DayplansFormProps> = ({ employees, change, ...props }) => (
  <div>
    <Prompt
      when={props.dirty && !props.submitSucceeded && !props.submitting}
      message="Vaše změny nebyly uloženy, přesto odejít?"
    />
    <form className="layout" onSubmit={props.handleSubmit}>
      <Card>
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>
          <Row>
            <Col md={8} xl={4} className="with-right-divider">
              <Row>
                <Col md={12}>
                  <h4>Cyklus směny</h4>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <RemoteAutoCompleteField
                    id="type"
                    name="shiftCycle"
                    label="Cyklus směny *"
                    fullWidth={true}
                    component={RemoteAutoComplete}
                    url="/metropolis/api/hr/workplan/shift/cycle"
                    labelFunction={o =>
                      o
                        ? `${get(o, 'label')} (${moment(get(o, 'from')).format('DD. MM. YYYY')} - ${moment(
                            get(o, 'to')
                          ).format('DD. MM. YYYY')})`
                        : undefined
                    }
                    valueFunction={o => o}
                    validate={[required]}
                    arrowstyle={{
                      color: '#e1e0e1',
                      position: 'absolute',
                      right: 0,
                      top: 32
                    }}
                    onChange={(_, val) => {
                      const employeesArray =
                        val && isArray(val.employees)
                          ? val.employees.map(employee => ({ employee, shiftCycle: val }))
                          : [];
                      change('employees', employeesArray);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <h4>Zaměstnanci</h4>
            </Col>
            <Col xs={12}>
              <FieldArray<any> name="employees" component={renderEmployees} employees={employees} change={change} />
            </Col>
          </Row>
        </Grid>

        <Button type="submit" label="Uložit a zavřít" primary={true} style={{ margin: '16px 0 16px 16px' }} />
        <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} containerElement={<Link to={linkBack} />} />
      </Card>
    </form>
  </div>
);

export const DayplansForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/hr-dayplans/DayplansForm.tsx